import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL;

const collectionSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Agent Shopping Spreadsheet Guides',
  description: 'Master the art of agent shopping with our comprehensive guides covering CSSBuy, Kakobuy, Superbuy, and Oopbuy spreadsheets.',
  url: `${siteUrl}/guides`,
  hasPart: [
    {
      '@type': 'Article',
      headline: 'CSSBuy Spreadsheet Complete Guide 2026',
      url: `${siteUrl}/article/cssbuy-spreadsheet-complete-guide`,
      datePublished: '2026-07-06',
    },
  ],
  publisher: {
    '@type': 'Organization',
    name: 'CSS Spreadsheet Hub',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png',
    },
  },
  inLanguage: 'en',
};

const guides = [
  {
    slug: 'cssbuy-spreadsheet-complete-guide',
    title: 'CSSBuy Spreadsheet Complete Guide 2026',
    excerpt: 'A deep dive into CSSBuy spreadsheets — learn how to find the best deals, evaluate quality, and build your first haul with confidence.',
    category: 'CSSBuy',
    readTime: '12 min',
    date: 'July 6, 2026',
  },
  {
    slug: '#',
    title: 'Kakobuy Spreadsheet Guide: Budget Finds & Tips',
    excerpt: 'Coming soon — master the Kakobuy spreadsheet ecosystem for budget-friendly fashion and streetwear hauls.',
    category: 'Kakobuy',
    readTime: 'Coming soon',
    date: '',
    coming: true,
  },
  {
    slug: '#',
    title: 'Superbuy Spreadsheet: Premium Shopping Strategy',
    excerpt: 'Coming soon — how to use Superbuy spreadsheets for high-value items with detailed QC photography.',
    category: 'Superbuy',
    readTime: 'Coming soon',
    date: '',
    coming: true,
  },
  {
    slug: '#',
    title: 'Oopbuy Spreadsheet: Beginner-Friendly Guide',
    excerpt: 'Coming soon — the easiest way to start agent shopping with Oopbuy spreadsheets and transparent fee structures.',
    category: 'Oopbuy',
    readTime: 'Coming soon',
    date: '',
    coming: true,
  },
  {
    slug: '#',
    title: 'Agent Shopping Comparison: CSSBuy vs Kakobuy vs Superbuy vs Oopbuy',
    excerpt: 'Coming soon — a comprehensive side-by-side analysis of all four major agent platforms.',
    category: 'Comparison',
    readTime: 'Coming soon',
    date: '',
    coming: true,
  },
  {
    slug: '#',
    title: 'How to Read a Spreadsheet: Quality Indicators & Red Flags',
    excerpt: 'Coming soon — learn to spot quality spreadsheets from low-effort lists before you waste money.',
    category: 'Tips',
    readTime: 'Coming soon',
    date: '',
    coming: true,
  },
];

export default function GuidesPage() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }} />
      <Navbar />
      <main className="w-full bg-background-50 pt-20 md:pt-24">
        <section className="w-full py-10 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-book-open-line text-xs"></i>
                </div>
                <span>Shopping Guides</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 mb-3">
                Agent Shopping Spreadsheet Guides
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                Master the art of agent shopping with our comprehensive guides covering CSSBuy, Kakobuy, Superbuy, and Oopbuy spreadsheets.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {guides.map((guide, idx) => (
                <div
                  key={idx}
                  className={`rounded-xl border border-background-200/60 bg-background-50 overflow-hidden transition-all duration-200 ${
                    guide.coming ? 'opacity-70' : 'hover:border-primary-200'
                  }`}
                >
                  {guide.coming ? (
                    <div className="p-5 md:p-6">
                      <span className="inline-block px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 text-xs font-medium mb-3">
                        {guide.category}
                      </span>
                      <h3 className="text-lg md:text-xl font-heading text-foreground-900 mb-2">
                        {guide.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                        {guide.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-600 bg-accent-50 px-3 py-1.5 rounded-full">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-time-line text-xs"></i>
                        </div>
                        Coming Soon
                      </span>
                    </div>
                  ) : (
                    <Link to={`/article/${guide.slug}`} className="block p-5 md:p-6 group">
                      <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
                        {guide.category}
                      </span>
                      <h3 className="text-lg md:text-xl font-heading text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">
                        {guide.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                        {guide.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-time-line text-xs"></i>
                          </div>
                          {guide.readTime}
                        </span>
                        {guide.date && <span>{guide.date}</span>}
                      </div>
                    </Link>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 rounded-full bg-background-50 border border-background-300/60 text-foreground-700 font-medium text-sm hover:bg-background-100 transition-all duration-200"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm"></i>
                </div>
                Back to Homepage
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}